import React, { useEffect, useState } from 'react';
import {
    CheckCircle2,
    CircleOff,
    Image as ImageIcon,
    ImageOff,
    LayoutPanelTop,
    Link2,
    Megaphone,
    Pause,
    PenLine,
    Play,
    Plus,
    Ruler,
    Sparkles,
    Trash2,
    Type,
    UploadCloud,
    X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SafeImage from '../../components/SafeImage';
import Select from '../../components/Select';
import ConfirmDialog from '../../components/ConfirmDialog';
import API from '../../utils/api';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import './Banners.css';


const BANNER_FORMATS = {
    middle: { label: 'Middle', dimensions: '1200 × 420', description: 'Contained editorial banner' },
    bottom: { label: 'Bottom', dimensions: '1200 × 300', description: 'Compact call-to-action strip' },
};

const POSITION_OPTIONS = Object.entries(BANNER_FORMATS).map(([value, format]) => ({
    value,
    label: format.label,
}));

const Banners = () => {
    const [banners, setBanners] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ title: '', subtitle: '', imageUrl: '', linkUrl: '', position: 'middle' });

    const resetForm = () => {
        setForm({ title: '', subtitle: '', imageUrl: '', linkUrl: '', position: 'middle' });
        setEditingId(null);
    };

    const startEdit = (banner) => {
        setForm({
            title: banner.title || '',
            subtitle: banner.subtitle || '',
            imageUrl: banner.imageUrl || '',
            linkUrl: banner.linkUrl || '',
            position: ['middle', 'bottom'].includes(banner.position) ? banner.position : 'middle',
        });
        setEditingId(banner._id);
        setShowForm(true);
    };

    useEffect(() => { fetchBanners(); }, []);

    const fetchBanners = async () => {
        try {
            const res = await API.get('/admin/banners');
            setBanners(res.data.banners || []);
        } catch (err) {
            toast.error('Failed to load banners');
        }
    };

    const updateForm = (field, value) => setForm(current => ({ ...current, [field]: value }));

    const handleImageUpload = async event => {
        const file = event.target.files[0];
        if (!file) return;
        if (!checkFiles([file], RULES.photo).length) {
            event.target.value = '';
            return;
        }
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        try {
            const res = await API.post('/admin/banners/upload-image', formData);
            const fullUrl = `${process.env.REACT_APP_BASE_URL || 'http://localhost:5000'}${res.data.imageUrl}`;
            updateForm('imageUrl', fullUrl);
            toast.success('Image uploaded!');
        } catch (err) {
            toast.error(uploadFailMessage(err, [file], RULES.photo));
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    const handleSubmit = async event => {
        event.preventDefault();
        setSubmitting(true);
        try {
            if (editingId) {
                await API.put(`/admin/banners/${editingId}`, form);
                toast.success('Banner updated!');
            } else {
                await API.post('/admin/banners', form);
                toast.success('Banner created!');
            }
            setShowForm(false);
            resetForm();
            fetchBanners();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save banner');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async id => {
        try {
            const res = await API.put(`/admin/banners/${id}/toggle`);
            toast.success(res.data.message);
            fetchBanners();
        } catch (err) {
            toast.error('Failed to update banner');
        }
    };

    const confirmDeleteBanner = async () => {
        const id = pendingDelete;
        if (!id) return;
        setPendingDelete(null);
        try {
            await API.delete(`/admin/banners/${id}`);
            toast.success('Banner deleted!');
            fetchBanners();
        } catch (err) {
            toast.error('Failed to delete banner');
        }
    };

    const activeCount = banners.filter(banner => banner.isActive).length;
    const selectedFormat = BANNER_FORMATS[form.position];

    return (
        <div className="banners-layout">
            <Sidebar />
            <div className="admin-content banners-content">
                <Navbar title={<span className="banners-nav-title"><Megaphone size={18} /> Banner management</span>} />

                <main className="banners-page">
                    <header className="banners-hero">
                        <div className="banners-hero__copy">
                            <span className="banners-eyebrow"><Sparkles size={14} /> Matrimony campaigns</span>
                            <h1>Homepage banners</h1>
                            <p>Create elegant promotional banners for featured matches, membership plans and wedding services.</p>
                        </div>
                        <div className="banners-hero__stats">
                            <div><strong>{banners.length}</strong><span>Total banners</span></div>
                            <div><strong>{activeCount}</strong><span>Active now</span></div>
                            <div className="banners-size"><Ruler size={19} /><span><b>4 layouts</b>Position-aware artwork</span></div>
                        </div>
                    </header>

                    <section className="banners-toolbar">
                        <div><h2>Campaign library</h2><p>{banners.length} {banners.length === 1 ? 'banner' : 'banners'} configured</p></div>
                        <button type="button" className={showForm ? 'is-cancel' : ''} onClick={() => { if (showForm) resetForm(); setShowForm(value => !value); }}>
                            {showForm ? <><X size={17} /> Close creator</> : <><Plus size={17} /> Create banner</>}
                        </button>
                    </section>

                    {showForm && (
                        <form className="banner-create" onSubmit={handleSubmit}>
                            <header className="banner-create__header">
                                <span><LayoutPanelTop size={19} /></span>
                                <div>
                                    <h2>{editingId ? 'Edit banner' : 'Create a new banner'}</h2>
                                    <p>{editingId ? 'Update the artwork or campaign details for this banner.' : 'Upload a wide image and add the campaign details shown to members.'}</p>
                                </div>
                            </header>

                            <div className="banner-create__body">
                                <div className="banner-create__media">
                                    <div className="banner-create__label"><ImageIcon size={14} /> Banner artwork <span>JPG or PNG · Max 5MB</span></div>
                                    {form.imageUrl ? (
                                        <div className={`banner-preview banner-preview--${form.position}`}>
                                            <SafeImage src={form.imageUrl} alt="New banner preview" />
                                            <div className="banner-preview__overlay"><span>{selectedFormat.dimensions} · {selectedFormat.label} preview</span></div>
                                            <button type="button" onClick={() => updateForm('imageUrl', '')}><Trash2 size={14} /> Remove</button>
                                        </div>
                                    ) : (
                                        <label className={`banner-upload ${uploading ? 'is-loading' : ''}`}>
                                            <input type="file" accept="image/*" disabled={uploading} onChange={handleImageUpload} />
                                            <span><UploadCloud size={27} /></span>
                                            <strong>{uploading ? 'Uploading artwork…' : 'Upload banner artwork'}</strong>
                                            <p>Choose a {selectedFormat.dimensions} image · {selectedFormat.description}</p>
                                            <p><b>Maximum file size: {RULES.photo.maxMB} MB</b> · {limitNote(RULES.photo)}</p>
                                            <small>Browse files</small>
                                        </label>
                                    )}
                                </div>

                                <div className="banner-create__fields">
                                    <BannerField icon={Type} label="Banner title" required>
                                        <input value={form.title} onChange={event => updateForm('title', event.target.value)} placeholder="Example: Find your perfect match" required />
                                    </BannerField>
                                    <BannerField icon={Type} label="Subtitle">
                                        <input value={form.subtitle} onChange={event => updateForm('subtitle', event.target.value)} placeholder="A short supporting message" />
                                    </BannerField>
                                    <BannerField icon={ImageIcon} label="Image URL">
                                        <input type="url" value={form.imageUrl} onChange={event => updateForm('imageUrl', event.target.value)} placeholder="https://example.com/banner.jpg" />
                                    </BannerField>
                                    <BannerField icon={Link2} label="Destination link">
                                        <input value={form.linkUrl} onChange={event => updateForm('linkUrl', event.target.value)} placeholder="/browse or https://…" />
                                    </BannerField>
                                    <BannerField icon={LayoutPanelTop} label="Display position">
                                        <Select
                                            value={form.position}
                                            onChange={value => updateForm('position', value)}
                                            options={POSITION_OPTIONS}
                                            allowEmpty={false}
                                            ariaLabel="Banner display position"
                                        />
                                        <small className="banner-position-hint"><Ruler size={12} /> {selectedFormat.dimensions} · {selectedFormat.description}</small>
                                    </BannerField>
                                </div>
                            </div>

                            <footer className="banner-create__footer">
                                <span><CheckCircle2 size={15} /> {editingId ? 'Changes are saved immediately.' : 'New banners are active immediately after creation.'}</span>
                                <div>
                                    <button type="button" className="secondary" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</button>
                                    <button type="submit" className="primary" disabled={submitting}>
                                        {editingId ? <PenLine size={16} /> : <Plus size={16} />} {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Create banner'}
                                    </button>
                                </div>
                            </footer>
                        </form>
                    )}

                    {banners.length > 0 ? (
                        <section className="banners-grid" aria-label="Configured banners">
                            {banners.map(banner => (
                                <article key={banner._id} className={`banner-card ${banner.isActive ? '' : 'is-inactive'}`}>
                                    <div className={`banner-card__media banner-card__media--${banner.position}`}>
                                        {banner.imageUrl
                                            ? <SafeImage src={banner.imageUrl} alt={banner.title} />
                                            : <div className="banner-card__placeholder"><ImageOff size={25} /><span>No artwork</span></div>}
                                        <span className={`banner-card__status ${banner.isActive ? 'is-active' : ''}`}>
                                            {banner.isActive ? <CheckCircle2 size={12} /> : <CircleOff size={12} />}
                                            {banner.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className="banner-card__position"><LayoutPanelTop size={12} /> {banner.position}</span>
                                    </div>
                                    <div className="banner-card__body">
                                        <h3>{banner.title}</h3>
                                        <p>{banner.subtitle || 'No subtitle provided'}</p>
                                        {banner.linkUrl && <span className="banner-card__link"><Link2 size={12} /> {banner.linkUrl}</span>}
                                        <div className="banner-card__actions">
                                            <button type="button" className="edit" onClick={() => startEdit(banner)}><PenLine size={14} /></button>
                                            <button type="button" className="toggle" onClick={() => handleToggle(banner._id)}>
                                                {banner.isActive ? <><Pause size={14} /> Deactivate</> : <><Play size={14} /> Activate</>}
                                            </button>
                                            <button type="button" className="delete" onClick={() => setPendingDelete(banner._id)}><Trash2 size={14} /> Delete</button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </section>
                    ) : !showForm && (
                        <section className="banners-empty">
                            <span><Megaphone size={31} /></span>
                            <h2>No banners yet</h2>
                            <p>Create your first matrimonial campaign banner to feature it on the customer website.</p>
                            <button type="button" onClick={() => setShowForm(true)}><Plus size={16} /> Create first banner</button>
                        </section>
                    )}
                </main>
            </div>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete banner?"
                message="This banner will be permanently removed from the campaign library."
                confirmLabel="Delete banner"
                danger
                onConfirm={confirmDeleteBanner}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

const BannerField = ({ icon: Icon, label, required, children }) => (
    <label className="banner-field">
        <span><Icon size={13} /> {label}{required && <b>*</b>}</span>
        {children}
    </label>
);

export default Banners;
